import React, { useState, useEffect } from 'react';
import './ModelingContainer.css';
import ContainerBox from './ContainerBox';
import { formatDuration } from '../utils/duration';

const ModelingContainer = ({
  analysis,
  visible,
  selectedTarget,
  droppedColumns,
  onReadyClick,
  onModelStatusChange
}) => {
  const [preliminaryResult, setPreliminaryResult] = useState(null);
  const [fullResult, setFullResult] = useState(null);
  const [preliminaryLoading, setPreliminaryLoading] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isPredicting, setIsPredicting] = useState(false);

  // Auto-prediction results (from full model training)
  const [predictionResult, setPredictionResult] = useState(null);

  // Container is ready when full model is trained
  const isReady = fullResult && !fullLoading;

  // Poll model-status while training is in progress
  useEffect(() => {
    if (!isPolling || !analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    const interval = setInterval(async () => {
      try {
        const { apiService } = await import('../services/api');
        const response = await apiService.getModelStatus(
          analysis.datasets[0].id,
          selectedTarget.name
        );
        const { full_result, full_status, prediction_result, prediction_pending } = response.data;

        if (full_status === 'ready' && full_result) {
          setFullResult(full_result);
          setFullLoading(false);

          if (prediction_pending) {
            // Training is done but auto-prediction is still running — keep polling
            setIsPredicting(true);
          } else {
            // Everything done
            if (prediction_result) setPredictionResult(prediction_result);
            setIsPredicting(false);
            if (onModelStatusChange) onModelStatusChange(selectedTarget.name, 'ready');
            setIsPolling(false);
          }
        } else if (full_status === 'error') {
          setError('Full model training failed. Please try again.');
          setIsPolling(false);
          setIsPredicting(false);
          setFullLoading(false);
        }
      } catch (err) {
        console.error('Full model status polling error:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, analysis?.datasets?.[0]?.id, selectedTarget?.name]);

  // Load persisted model results when container becomes visible OR when target changes
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && selectedTarget?.name) {
      // Stop any in-progress polling for the previous target
      setIsPolling(false);
      setIsPredicting(false);
      // Reset model state when target changes
      setPreliminaryResult(null);
      setFullResult(null);
      setPredictionResult(null);
      setIsStale(false);
      setError(null);

      // Load results for the new target
      loadPersistedResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, analysis?.datasets, selectedTarget?.name]);

  // Re-check status when columns are dropped/added
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && selectedTarget?.name) {
      loadPersistedResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedColumns]);

  // Automatically train preliminary model if no persisted results and not stale
  useEffect(() => {
    if (visible && !preliminaryResult && !preliminaryLoading && !fullResult && !isStale && selectedTarget && analysis?.datasets?.[0]?.id) {
      // Small delay to allow persisted results to load first
      const timer = setTimeout(() => {
        if (!preliminaryResult && !fullResult && !isStale) {
          trainPreliminaryModel();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, preliminaryResult, fullResult, isStale, selectedTarget?.name]);

  const loadPersistedResults = async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getModelStatus(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      const { preliminary_result, preliminary_status, full_result, full_status, prediction_result, prediction_pending } = response.data;

      // Check if any artifacts are stale
      const hasStale = preliminary_status === 'stale' || full_status === 'stale';
      setIsStale(hasStale);

      // If training is in progress, start polling
      if (full_status === 'pending') {
        setFullLoading(true);
        setIsPolling(true);
        return;
      }

      // Always load results even if stale, so user can see old results until they refresh
      if (preliminary_result) {
        setPreliminaryResult(preliminary_result);
      }
      if (full_result) {
        setFullResult(full_result);
      }
      // Load persisted prediction results
      if (prediction_result) {
        setPredictionResult(prediction_result);
      }

      // If auto-prediction is still running (training done, auto-predict in progress), start polling
      if (prediction_pending) {
        setIsPredicting(true);
        setIsPolling(true);
      }
    } catch (err) {
      console.error('Failed to load persisted model results:', err);
      // Don't show error to user, just fail silently and train new model
    }
  };

  const trainPreliminaryModel = async () => {
    setPreliminaryLoading(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.trainPreliminaryModel(
        analysis.datasets[0].id,
        selectedTarget.name
      );
      // For engineered targets, the result contains method and message
      // For regular targets, result contains training metrics
      setPreliminaryResult(response.data.result);
      console.log('Preliminary result:', response.data.result);
    } catch (err) {
      setError('Failed to train preliminary model. Please try again.');
      console.error('Preliminary model training error:', err);
    } finally {
      setPreliminaryLoading(false);
    }
  };

  const handleTrainFull = async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget) return;

    setFullLoading(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.trainFullModel(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      if (response.status === 202) {
        // Training started in background — poll until complete
        setIsPolling(true);
        return; // fullLoading stays true; polling will clear it
      }

      // Synchronous response (backward compat path)
      if (response.data.is_engineered && response.data.method === 'linear_extrapolation') {
        if (response.data.prediction_result) {
          setPredictionResult(response.data.prediction_result);
        }
      } else {
        setFullResult(response.data.result);
        if (response.data.prediction_result) {
          setPredictionResult(response.data.prediction_result);
        }
      }

      if (onModelStatusChange) {
        onModelStatusChange(selectedTarget.name, 'ready');
      }
      setFullLoading(false);
    } catch (err) {
      setError('Failed to train full model. Please try again.');
      console.error('Full model training error:', err);
      setFullLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    setRefreshing(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');

      // Clear full model state before refresh to ensure clean slate
      setFullResult(null);

      // Refresh modeling artifacts (trains preliminary model, deletes all full model artifacts)
      await apiService.refreshModelingArtifacts(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      // Reload results
      await loadPersistedResults();

      // Clear stale flag
      setIsStale(false);
    } catch (err) {
      setError('Failed to refresh modeling artifacts. Please try again.');
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const formatMetric = (value, metricName) => {
    if (value === undefined || value === null) {
      return 'N/A';
    }
    if (metricName === 'accuracy') {
      return `${(value * 100).toFixed(1)}`;
    } else if (metricName === 'MAPE') {
      return `${value.toFixed(1)}`;
    } else if (metricName === 'MAE') {
      return value.toFixed(2);
    }
    return value.toFixed(4);
  };

  // MAE is in the target's units, not a percentage — suppress the % suffix.
  const isPercentMetric = (metricName) => metricName === 'accuracy' || metricName === 'MAPE' || metricName === 'Accuracy';

  const metricTooltip = (metricName) => {
    if (metricName === 'MAPE') return 'Mean Absolute Percentage Error: Average percentage difference between predictions and actual values.';
    if (metricName === 'MAE') return "Mean Absolute Error: Average absolute difference between predictions and actual values, in the target's units.";
    if (metricName === 'Accuracy' || metricName === 'accuracy') return 'Classification Accuracy: The proportion of total predictions that were correct.';
    return metricName; // Fallback if the metric name is unknown
  };

  const getPerformanceIndication = (value, metricName) => {
    // The word "Performance" is always added without a preceeding whitespace.
    if (value === undefined || value === null) {
      return '';
    }
    if (metricName === 'accuracy') {
      if (value > .9) {
          return "Excellent ";
      } else if (value >= .8) { // 80-90%
          return "Good ";
      } else if (value >= .7) { // 70-80%
          return "Acceptable ";
      } else if (value >= .6) { // 60-70%
          return "Rather Low ";
      } else { // <60%
          return "Poor ";
      }
    } else if (metricName === 'MAPE') {
      if (value <= 10) {
          return "Excellent ";
      } else if (value <= 25) { // 10-25%
          return "Good ";
      } else if (value <= 40) { // 25-40%
          return "Acceptable ";
      } else if (value <= 60) { // 40-60%
          return "Rather Low ";
      } else { // >60%
          return "Poor ";
      }
    } else {
      return "";
    }
  }

  // Download predictions handler
  const handleDownloadPredictions = async () => {
    if (!predictionResult?.download_url) return;

    try {
      const { default: api } = await import('../services/api');
      // Construct the full URL (handle both relative and absolute URLs)
      const serverOrigin = new URL(api.defaults.baseURL).origin;
      const downloadUrl = predictionResult.download_url.startsWith('http')
        ? predictionResult.download_url
        : `${serverOrigin}${predictionResult.download_url}`;

      // Fetch the file as a blob to ensure we get the actual CSV content
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = predictionResult.filename || 'predictions.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download predictions. Please try again.');
    }
  };

  if (!visible) return null;

  return (
    <ContainerBox
      title="Make Predictions"
      isReady={isReady}
      scrollToNext={true}
      readyLabel='Explainability'
      onReadyClick={onReadyClick}
      isStale={isStale}
      onRefreshClick={handleRefresh}
      refreshing={refreshing}
    >
      {error && (
        <div className="modeling-error">
          <p>{error}</p>
        </div>
      )}

      {/* Show loading state when refreshing */}
      {refreshing && (
        <div className="modeling-loading">
          <div className="loading-spinner"></div>
          <p>Refreshing model...</p>
        </div>
      )}

      {/* Show loading state when preliminary model is training */}
      {preliminaryLoading && !refreshing && (
        <div className="modeling-loading">
          <div className="loading-spinner"></div>
          <p>Training preliminary model...</p>
        </div>
      )}

      {/* Results of preliminary and full model training */}
      {/* Skip showing training results for engineered targets - they go straight to predictions */}
      {!(preliminaryResult && preliminaryResult.method === 'linear_extrapolation') && (
      <div className='modeling-section-results-container-wrapper' style={{ marginBottom: !fullResult && !predictionResult ? 0 : undefined }}>
        {/* Preliminary Model Results */}
        {preliminaryResult && (
          <>
              {/* Preliminary Model Results */}
              <div className='modeling-section-results-container'>
                <h3>Preliminary Model</h3>
                <div className='modeling-section-result-container-header'>
                  <span className="modeling-results__badge">
                    {preliminaryResult.task_type?.toLowerCase() === 'classification' ? 'Classification' : 'Regression'}
                  </span>
                  <span className="modeling-results__time">
                    Trained in {formatDuration(preliminaryResult.training_time_sec.toFixed(1))}
                  </span>
                </div>

                  <div className='modeling-section-container-performance-indication'>
                    {getPerformanceIndication(preliminaryResult.test_metric, preliminaryResult.test_metric_name)}Performance
                    {getPerformanceIndication(preliminaryResult.test_metric, preliminaryResult.test_metric_name) === 'Acceptable ' ? <div className='modeling-section-container-performance-indication-note'>Depending on the use-case, this is sufficient.</div> : <></>}
                    {getPerformanceIndication(preliminaryResult.test_metric, preliminaryResult.test_metric_name) === 'Rather Low ' ? <div className='modeling-section-container-performance-indication-note'>Try adding more data that might be useful for predicting the target -  more samples and/or columns.</div> : <></>}
                  </div>

                  <div className='modeling-section-result-container-content'>
                    {/* Test result */}
                    <div className='modeling-section-test-result-container'>
                      <div className='modeling-section-test-result-value'>
                        {formatMetric(preliminaryResult.test_metric, preliminaryResult.test_metric_name)}
                        {isPercentMetric(preliminaryResult.test_metric_name) && <p className='modeling-section-test-result-percent'>%</p>}
                      </div>
                      <div
                      className='modeling-section-result-metric'
                      title={metricTooltip(preliminaryResult.test_metric_name)}
                      >
                        Test {preliminaryResult.test_metric_name}
                      </div>
                    </div>
                    {/* Train result */}
                    <div className='modeling-section-train-result-container'>
                      <div className='modeling-section-train-result-value'>
                        {formatMetric(preliminaryResult.train_metric, preliminaryResult.train_metric_name)}
                        {isPercentMetric(preliminaryResult.train_metric_name) && <p className='modeling-section-train-result-percent'>%</p>}
                      </div>
                      <div
                      className='modeling-section-result-metric'
                      title={metricTooltip(preliminaryResult.train_metric_name)}
                      >
                        Training {preliminaryResult.train_metric_name}
                      </div>
                    </div>
                  </div>

                  <div className='modeling-section-sample-usage-info'>
                    {preliminaryResult.n_train_samples.toLocaleString()} samples used for training, {preliminaryResult.n_test_samples.toLocaleString()} samples for testing.
                  </div>

                </div>
          </>
        )}

        {/* Full Model Results */}
          <div className='modeling-section-results-container'>


            {/* Train full model button in case no model has been trained yet */}
            {!fullResult && !(preliminaryResult && preliminaryResult.method === 'linear_extrapolation') && (
              <div className="modeling-button-container">
                <button
                  className="btn btn-primary modeling-train-full-button"
                  onClick={handleTrainFull}
                  disabled={fullLoading}
                >
                  {fullLoading ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      Training Full Model...
                    </>
                  ) : (
                    'Train Full Model'
                  )}
                </button>
              </div>
            )}
            
            {/* For engineered targets, show a button to generate predictions */}
            {preliminaryResult && preliminaryResult.method === 'linear_extrapolation' && !predictionResult && (
              <div className="modeling-button-container">
                <button
                  className="btn btn-primary modeling-train-full-button"
                  onClick={handleTrainFull}
                  disabled={fullLoading}
                >
                  {fullLoading ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      Generating Predictions...
                    </>
                  ) : (
                    'Generate Predictions'
                  )}
                </button>
              </div>
            )}

            {/* Full Model Results */}
            {fullResult && (
              <>
                {/* Full Model Results */}
                <h3>Full Model</h3>
                <div className='modeling-section-result-container-header'>
                  <span className="modeling-results__badge">
                    {fullResult.task_type?.toLowerCase() === 'classification' ? 'Classification' : 'Regression'}
                  </span>
                  <span className="modeling-results__time">
                    Trained in {formatDuration(fullResult.training_time_sec.toFixed(1))}
                  </span>
                </div>

                <div className='modeling-section-container-performance-indication'>
                  {getPerformanceIndication(fullResult.test_metric, fullResult.test_metric_name)}Performance
                  {getPerformanceIndication(fullResult.test_metric, fullResult.test_metric_name) === 'Acceptable ' ? <div className='modeling-section-container-performance-indication-note'>Depending on the use-case, this is sufficient.</div> : <></>}
                  {getPerformanceIndication(fullResult.test_metric, fullResult.test_metric_name) === 'Rather Low ' ? <div className='modeling-section-container-performance-indication-note'>Try adding more data that might be useful for predicting the target -  more samples and/or columns.</div> : <></>}
                </div>

                <div className='modeling-section-result-container-content'>
                  {/* Test result */}
                  <div className='modeling-section-test-result-container'>
                    <div className='modeling-section-test-result-value'>
                      {formatMetric(fullResult.test_metric, fullResult.test_metric_name)}
                      {isPercentMetric(fullResult.test_metric_name) && <p className='modeling-section-test-result-percent'>%</p>}
                    </div>
                    <div
                    className='modeling-section-result-metric'
                    title={metricTooltip(fullResult.test_metric_name)}
                    >
                      Test {fullResult.test_metric_name}
                    </div>
                  </div>
                  {/* Train result */}
                  <div className='modeling-section-train-result-container'>
                    <div className='modeling-section-train-result-value'>
                      {formatMetric(fullResult.train_metric, fullResult.train_metric_name)}
                      {isPercentMetric(fullResult.train_metric_name) && <p className='modeling-section-train-result-percent'>%</p>}
                    </div>
                    <div
                    className='modeling-section-result-metric'
                    title={metricTooltip(fullResult.train_metric_name)}
                    >
                      Training {fullResult.train_metric_name}
                    </div>
                  </div>
                </div>

                <div className='modeling-section-sample-usage-info'>
                  {fullResult.n_train_samples.toLocaleString()} samples used for training, {fullResult.n_test_samples.toLocaleString()} samples for testing.
                </div>
              </>
            )}
          </div>
      </div>
      )}

      {/* For engineered targets, show button container outside the hidden training section */}
      {preliminaryResult && preliminaryResult.method === 'linear_extrapolation' && !predictionResult?.has_predictions && (
        <div className='modeling-section-results-container-wrapper'>
          <div className='modeling-section-results-container'>
            <div className="modeling-button-container">
              <button
                className="btn btn-primary modeling-train-full-button"
                onClick={handleTrainFull}
                disabled={fullLoading}
              >
                {fullLoading ? (
                  <>
                    <div className="loading-spinner-small"></div>
                    Generating Predictions...
                  </>
                ) : (
                  'Generate Predictions'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show predicting spinner while auto-prediction runs in background */}
      {isPredicting && !predictionResult?.has_predictions && (
        <div className="modeling-predicting-indicator">
          <div className="loading-spinner-small loading-spinner-small--grey"></div>
          <span className="modeling-predicting-label">Predicting...</span>
        </div>
      )}

      {/* Show predictions section when predictions exist (for both regular and engineered targets) */}
      {predictionResult?.has_predictions && (
        <div className="predictions-section">
          <div className="predictions-header">
            <h3>Complete Dataset with Predictions</h3>
            <div className="predictions-info">
              <span className="predictions-count">
                {predictionResult.n_predictions?.toLocaleString()} values predicted
              </span>
              <span className="predictions-total">
                ({predictionResult.n_total_rows?.toLocaleString()} total rows)
              </span>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleDownloadPredictions}
              >
                Download
              </button>
            </div>
              </div>

          {/* Preview Table */}
          {predictionResult.preview && (
            <div className="predictions-preview">
              <p className="predictions-preview-note">
                Preview of predicted rows (highlighted cells show predicted "{predictionResult.target_column}" values):
              </p>
              <div className="predictions-table-wrapper">
                <table className="predictions-table">
                  <thead>
                    <tr>
                      {predictionResult.preview.columns?.map((col, i) => (
                        <th 
                          key={i} 
                          className={col === predictionResult.preview.predicted_column ? 'predicted-column' : ''}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {predictionResult.preview.rows?.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {row.map((cell, cellIdx) => {
                          const colName = predictionResult.preview.columns?.[cellIdx];
                          const isPredictedCell = colName === predictionResult.preview.predicted_column;
                          const cellStr = String(cell ?? '').toLowerCase();
                          const isPositive = cellStr === 'yes' || cellStr === 'true' || cellStr === '1';
                          const isNegative = cellStr === 'no' || cellStr === 'false' || cellStr === '0';
                          
                          return (
                            <td 
                              key={cellIdx}
                              className={
                                isPredictedCell 
                                  ? (isPositive ? 'predicted-value predicted-positive' : 
                                     isNegative ? 'predicted-value predicted-negative' : 
                                     'predicted-value')
                                  : ''
                              }
                            >
                              {typeof cell === 'number' ? 
                                (Number.isInteger(cell) ? cell : cell.toFixed(2)) : 
                                String(cell ?? '')}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                  </div>
                )}
              </div>
            )}

      {/* Show message when no predictions available */}
      {predictionResult && !predictionResult.has_predictions && (
        <div className="predictions-section predictions-section--empty">
          <h3>Predictions</h3>
          <p className="predictions-empty-message">
            {predictionResult.reason || 'No rows available for prediction. All rows in your dataset have known target values. If you upload a dataset with missing target values, we automatically generate predictions for them.'}
          </p>
          </div>
      )}

    </ContainerBox>
  );
};

export default ModelingContainer;

